import { getLeadOpportunitySource } from "../../leadSourceHelper";
import { getIncludedRecords, resolveSelectedIndex } from "./mergeRecordsHelpers";

/**
 * The mergeable lead fields, in design order (EAI-1816, moved onto `mergeLead`
 * for EAI-1871).
 *
 * `value` renders the comparison cell; `variables` contributes the winning
 * record's slice of the `mergeLead` payload. A field marked `combined` is shown
 * for comparison but cannot be picked — see below.
 *
 * ── What the server picks, and what it combines ──────────────────────────────
 * `mergeLead` keeps the master lead and updates it in place, so only the
 * columns that can hold exactly one value are picked:
 *
 *   name, location            scalar — the pick writes the column
 *   phone, email, address     every row still carries over to the master, and
 *                             the pick says which of them stay ACTIVE: the rest
 *                             are flagged inactive in place, never moved or
 *                             deleted, so the payload is the full list of row
 *                             ids the winning record holds
 *   source                    never written — `lead_source_type` records how
 *                             the lead reached us, and rewriting it on the
 *                             survivor would falsify the record's origin
 *
 * Source stays on screen because seeing what the merged lead will end up
 * holding is the point of the comparison; it simply does not offer a choice.
 */

/** Primary contact first, matching how the lead profile orders them. */
const primaryFirst = items =>
  [...(items || [])].sort((a, b) => (b?.isPrimary ? 1 : 0) - (a?.isPrimary ? 1 : 0));

/**
 * Every row id a picked record holds, for phones / emails / addresses.
 *
 * The merge carries all of them onto the master and then deactivates the ones
 * the user did not pick, so it needs the whole list: sending only the leading
 * row lost every other number the record had.
 *
 * `undefined` when the record has none, so `omitUndefined` drops the argument —
 * an empty list would read as "deactivate all of them".
 */
const contactRowIds = items => {
  const ids = (items || []).map(item => Number(item?.id)).filter(Number.isFinite);
  return ids.length ? ids : undefined;
};

/** "1284 Willow Ave, Sunnyvale, CA 94086" from a lead address row. */
export const formatLeadAddress = address => {
  if (!address) return "";
  const stateAndZip = [address.state, address.postalCode].filter(Boolean).join(" ");
  return [address.addressLine1, address.addressLine2, address.city, stateAndZip, address.country]
    .filter(Boolean)
    .join(", ");
};

export const LEAD_MERGE_FIELDS = [
  {
    key: "name",
    langKey: "name",
    value: lead => lead?.fullName || [lead?.firstName, lead?.lastName].filter(Boolean).join(" "),
    // Both are optional on `mergeLead`: an argument left out leaves the
    // master's value alone, so only send what the record actually has.
    variables: lead => ({
      firstName: lead?.firstName || undefined,
      lastName: lead?.lastName || undefined,
    }),
    // One cell, two columns — the audit records both.
    sourceFields: ["first_name", "last_name"],
  },
  {
    key: "phone",
    langKey: "mergePhone",
    value: lead =>
      primaryFirst(lead?.phoneNumbers)
        .map(item => item?.phone)
        .filter(Boolean)
        .join(", "),
    variables: lead => ({ keepPhoneIds: contactRowIds(lead?.phoneNumbers) }),
  },
  {
    key: "email",
    langKey: "mergeEmail",
    value: lead =>
      primaryFirst(lead?.emails)
        .map(item => item?.email)
        .filter(Boolean)
        .join(", "),
    variables: lead => ({ keepEmailIds: contactRowIds(lead?.emails) }),
  },
  {
    key: "address",
    langKey: "mergeAddress",
    value: lead => formatLeadAddress(lead?.addresses?.[0]),
    // A real pick now that addresses can be deactivated. Deliberately no
    // `sourceFields`: that feeds `valueSources`, which is keyed by column name,
    // and a list of row ids names no column — phone and email omit it too.
    variables: lead => ({ keepAddressIds: contactRowIds(lead?.addresses) }),
  },
  {
    key: "location",
    langKey: "location",
    value: lead => lead?.location?.title,
    variables: lead => ({ locationId: lead?.location?.id ? +lead.location.id : undefined }),
    sourceFields: ["location_id"],
  },
  {
    key: "source",
    langKey: "source",
    // Not mergeable by design — the survivor keeps its own origin.
    combined: true,
    value: lead => getLeadOpportunitySource(lead?.leadSource),
  },
];

/** The fields that actually contribute a value, i.e. everything pickable. */
const pickableFields = LEAD_MERGE_FIELDS.filter(field => !field.combined);

/**
 * Drops keys with no value, so an argument the winner had nothing for is truly
 * absent from the payload.
 *
 * An omitted argument leaves the master's column alone, which is the behaviour
 * wanted; `{ locationId: undefined }` happens to serialize away too, but only
 * as a side effect of JSON encoding. Being explicit keeps that from depending
 * on how the transport happens to treat undefined.
 */
const omitUndefined = object =>
  Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined));

/**
 * The full `mergeLead` payload for the current selection.
 *
 * `masterLeadId` is the record the user made master — it survives. `mergeLeadIds`
 * is every other participating record; excluded ones are left out rather than
 * flagged, which is what keeps them live.
 *
 * `valueSources` records which lead each winning value came from, named by the
 * column it writes so the audit reads in the server's own terms. Only the
 * scalar fields declare one: a phone, email or address pick sends row ids to
 * keep active rather than writing a column, so it names none.
 */
export const buildLeadMergeVariables = ({ records, primaryIdx, excluded, picks }) => {
  const included = getIncludedRecords(records, primaryIdx, excluded);
  const master = records?.[primaryIdx];

  const winnerFor = fieldKey =>
    records?.[resolveSelectedIndex({ picks, fieldKey, included, primaryIdx })];

  // Built once per field so `valueSources` can be derived from what was
  // actually sent: a winner with nothing in the column contributes no argument,
  // and must not then be credited in the audit for a value the merge never
  // wrote.
  const contributions = pickableFields.map(field => ({
    field,
    winner: winnerFor(field.key),
    variables: field.variables(winnerFor(field.key)),
  }));

  const fieldVariables = omitUndefined(
    contributions.reduce((acc, { variables }) => ({ ...acc, ...variables }), {})
  );

  const valueSources = contributions
    .filter(({ variables }) => Object.values(variables).some(value => value !== undefined))
    .flatMap(({ field, winner }) =>
      (field.sourceFields || []).map(sourceField => ({
        field: sourceField,
        leadId: Number(winner?.id),
      }))
    )
    .filter(source => Number.isFinite(source.leadId));

  return {
    masterLeadId: Number(master?.id),
    // The master travels as its own argument and must not appear here too.
    mergeLeadIds: (records || [])
      .map((record, index) => (index !== primaryIdx && included[index] ? Number(record?.id) : null))
      .filter(Boolean),
    ...fieldVariables,
    valueSources,
  };
};
